import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropdown from '@components/dropdown/Dropdown';
import { setResizeParams } from '@app/actions/customizeActions';
import LocalizedText from '@LocalizedText';
import { getShapeByCornerType } from '@app/utils/utils';
import { CORNER_TYPES_CONFIG, SCALLOPED_CORNER_MAPPING } from '@app/utils/shapeConfigs';
import { CORNER_TYPES } from '@app/constants';

const ShapeSelect = ({ resizeParams, setResizeParams, matrix, disabled, placeholder, cornerTypeOptionsEnabled, currentFlow }) => {
  const {
    selectedShape,
    isShapeChanged,
    currentCornerType,
    selectedCornerType,
    shapeByCornerType,
    shapeList = {},
    flatShapeList = [],
    createdFromDielineShape,
  } = resizeParams;
  const {
    ctx: { shape: currentShape },
  } = matrix;

  const shapeOptions = useMemo(() => {
    const nonScallopShapes = flatShapeList.filter(item => !item.shape.startsWith('scallop_'));

    return nonScallopShapes.map(({ shape, iconUrlNew }) => {
      const cornerTypeLocKey = ['circle', 'ellipse'].includes(shape) ? 'qau-edge-type-title' : 'qau-corner-type-title';
      const cornerTypeAriaLabelLocaleKey = ['circle', 'ellipse'].includes(shape) ? 'qau-edge-type-dropdown-aria-label' : 'qau-corner-type-dropdown-aria-label';

      return { value: shape, icon: iconUrlNew, cornerTypeLocKey, cornerTypeAriaLabelLocaleKey };
    });
  }, [shapeList, createdFromDielineShape]);

  const cornerTypeOptions = useMemo(() => {
    const shapesWithScallopSupport = new Set(shapeList?.scallopShapes?.map(shape => shape.shape.replace('scallop_', '')));

    return Object.entries(CORNER_TYPES_CONFIG).reduce((acc, [type, config]) => {
      if (config.conditions.includes(selectedShape)) {
        if ([CORNER_TYPES.SCALLOPED_EDGE, CORNER_TYPES.SCALLOPED_CORNER].includes(type) && !shapesWithScallopSupport.has(selectedShape.replace('scallop_', ''))) {
          return acc;
        }
        acc.push({ value: type, icon: config.icon });
      }
      return acc;
    }, []);
  }, [shapeList, selectedShape]);

  const isValidCornerType = (cornerType, shape) => {
    return cornerType && shape && CORNER_TYPES_CONFIG[cornerType].conditions.includes(shape);
  };

  const defaultCornerType = useMemo(() => {
    return Object.entries(CORNER_TYPES_CONFIG).find(([type, config]) => config.defaultForShapes?.includes(selectedShape))?.[0];
  }, [resizeParams]);

  const selectedShapeOption = useMemo(() => {
    const shape = selectedShape?.replace('scallop_', '');
    return shapeOptions.find(option => option.value === shape);
  }, [resizeParams]);

  const selectedCornerTypeOptions = useMemo(() => {
    let cornerType = selectedCornerType || currentCornerType;

    if (SCALLOPED_CORNER_MAPPING[cornerType] && !CORNER_TYPES_CONFIG[cornerType].conditions.includes(selectedShape)) {
      cornerType = SCALLOPED_CORNER_MAPPING[cornerType];
    }

    cornerType = isValidCornerType(cornerType, selectedShape) ? cornerType : defaultCornerType;

    return cornerTypeOptions.find(option => option.value === cornerType);
  }, [resizeParams]);

  const onShapeSelect = ({ value: shape }) => {
    setResizeParams({
      isShapeChanged: currentShape !== shape,
      prevSelectedShape: selectedShape,
      selectedShape: shape,
    });
  };

  const onCornerTypeSelect = ({ value: cornerType }) => {
    setResizeParams({
      isCornerTypeChanged: currentCornerType !== cornerType,
      selectedCornerType: cornerType,
    });
  };

  useEffect(() => {
    const newShapeByCornerType = getShapeByCornerType(selectedShape, selectedCornerType, shapeList);

    setResizeParams({
      prevShapeByCornerType: shapeByCornerType,
      shapeByCornerType: newShapeByCornerType,
    });
  }, [selectedCornerType, selectedShape, setResizeParams]);

  useEffect(() => {
    if (!selectedCornerType && isShapeChanged) {
      setResizeParams({
        selectedCornerType: defaultCornerType,
        isCornerTypeChanged: selectedCornerType !== defaultCornerType,
      });
    }
  }, [defaultCornerType, isShapeChanged, selectedCornerType, selectedShape, setResizeParams]);

  return (
    <div aria-disabled={!selectedShapeOption || disabled}>
      <div className="tool-group-label">
        <LocalizedText id={`qau-${currentFlow}-shape-title`} tagName="b" />
      </div>
      <Dropdown
        ariaLabelLocaleKey={`qau-${currentFlow}-shape-dropdown-aria-label`}
        options={shapeOptions}
        onSelect={onShapeSelect}
        selected={selectedShapeOption}
        placeholder={placeholder}
      />
      {cornerTypeOptionsEnabled && cornerTypeOptions.length > 1 && !disabled && (
        <>
          <div className="tool-group-label">
            <LocalizedText id={`${selectedShapeOption?.cornerTypeLocKey}`} tagName="b" />
          </div>
          <Dropdown
            ariaLabelLocaleKey={selectedShapeOption?.cornerTypeAriaLabelLocaleKey}
            options={cornerTypeOptions}
            onSelect={onCornerTypeSelect}
            selected={selectedCornerTypeOptions}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({
  customize: {
    customizeData: { resizeParams },
  },
  matrix,
}) => ({
  resizeParams,
  matrix,
});

const mapDispatchToProps = {
  setResizeParams,
};

ShapeSelect.propTypes = {
  matrix: PropTypes.object,
  resizeParams: PropTypes.object,
  setResizeParams: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  cornerTypeOptionsEnabled: PropTypes.bool,
  currentFlow: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShapeSelect);
